// Question definitions for the candidate reference questionnaire.
// Two sets: executive (C-suite) and non_executive.
// Each question has: id, section, sectionIntro (optional), label, text, type (textarea | radio | rating)

export const EXECUTIVE_QUESTIONS = [
  // Section 1: Relationship & Executive Scope
  {
    id: "q1",
    section: "Section 1: Relationship & Executive Scope",
    label: "Governance & Context",
    text: "In what capacity did you work with the candidate, and during what period? What was the organizational structure surrounding your working relationship?",
    type: "textarea",
  },
  {
    id: "q2",
    section: "Section 1: Relationship & Executive Scope",
    label: "Scope of Authority",
    text: "What was the scale of business, financial responsibility (P&L/budget size), and headcount directly under the candidate's purview during your tenure together?",
    type: "textarea",
  },
  // Section 2: Strategic Vision & Luxury Brand Stewardship
  {
    id: "q3",
    section: "Section 2: Strategic Vision & Luxury Brand Stewardship",
    sectionIntro:
      "At the C-suite level within Moët Hennessy and the wider LVMH ecosystem, leaders must balance entrepreneurial agility with long-term brand equity (\"The Art of Crafting Dreams\") rather than sacrificing heritage for short-term gains.",
    label: "Long-Term Vision vs. Short-Term Pressure",
    text: "Can you provide an example of how the candidate navigated strategic trade-offs — specifically balancing immediate financial/commercial performance with long-term brand desirability and prestige?",
    type: "textarea",
  },
  {
    id: "q4",
    section: "Section 2: Strategic Vision & Luxury Brand Stewardship",
    label: "Entrepreneurial Mindset in a Decentralized Model",
    text: "LVMH operates as a decentralized federation of Maisons. How effectively does the candidate operate autonomously as an entrepreneur while remaining aligned with broader group priorities?",
    type: "textarea",
  },
  {
    id: "q5",
    section: "Section 2: Strategic Vision & Luxury Brand Stewardship",
    label: "Innovation & Craftsmanship",
    text: "How does the candidate foster innovation, creative vision, and digital/commercial evolution without compromising the core heritage, craftsmanship, and luxury positioning of the business?",
    type: "textarea",
  },
  // Section 3: Enterprise Leadership & Operational Execution
  {
    id: "q6",
    section: "Section 3: Enterprise Leadership & Operational Execution",
    label: "P&L & Commercial Acumen",
    text: "How would you rate the candidate's strategic commercial capability, capital allocation rigor, and performance during economic headwinds or market shifts?",
    type: "textarea",
  },
  {
    id: "q7",
    section: "Section 3: Enterprise Leadership & Operational Execution",
    label: "Cross-Functional & Global Governance",
    text: "How adept is the candidate at leading across complex matrix structures, international markets, and highly diverse cultural landscapes?",
    type: "textarea",
  },
  {
    id: "q8",
    section: "Section 3: Enterprise Leadership & Operational Execution",
    label: "Decisive Crisis Management",
    text: "Describe a high-stakes crisis or disruption where the candidate was required to make difficult executive decisions. How did they lead their team and protect business continuity?",
    type: "textarea",
  },
  // Section 4: Integrity, Discretion & High-Stakes Governance
  {
    id: "q9",
    section: "Section 4: Integrity, Discretion & High-Stakes Governance",
    sectionIntro:
      "C-level executives at Moët Hennessy represent legendary Maisons in front of global heads of state, high-net-worth individuals (HNWIs), trade partners, and the press.",
    label: "Discretion & Reputation Management",
    text: "Luxury leadership requires absolute discretion. How has the candidate demonstrated flawless ethical judgment, confidentiality, and brand protection when handling sensitive corporate matters or elite client relationships?",
    type: "textarea",
  },
  {
    id: "q10",
    section: "Section 4: Integrity, Discretion & High-Stakes Governance",
    label: "Executive Presence & External Representation",
    text: "How does the candidate perform when acting as the face of an organization in high-profile public forums, media engagements, or board meetings?",
    type: "textarea",
  },
  {
    id: "q11",
    section: "Section 4: Integrity, Discretion & High-Stakes Governance",
    label: "Corporate Social Responsibility (CSR) & ESG Commitment",
    text: "LVMH prioritizes environmental sustainability (e.g., LIFE 360) and social impact. How has the candidate integrated sustainability, ethical sourcing, or social responsibility into their business strategy?",
    type: "textarea",
  },
  // Section 5: Talent Magnetism & Cultural Leadership
  {
    id: "q12",
    section: "Section 5: Talent Magnetism & Cultural Leadership",
    label: "Executive Talent Attraction & Retention",
    text: "How successful is the candidate at building, mentoring, and retaining top-tier executive talent and creative leaders?",
    type: "textarea",
  },
  {
    id: "q13",
    section: "Section 5: Talent Magnetism & Cultural Leadership",
    label: "Diversity, Equity & Inclusion (DEI)",
    text: "How has the candidate actively fostered an inclusive, psychologically safe culture across four generations and diverse international talent bases?",
    type: "textarea",
  },
  {
    id: "q14",
    section: "Section 5: Talent Magnetism & Cultural Leadership",
    label: "Authenticity & Peer Influence",
    text: "How would you describe the candidate's authentic leadership style and their ability to influence peers and board members through credibility rather than authority alone?",
    type: "textarea",
  },
  // Section 6: Executive Endorsement & Final Assessment
  {
    id: "q15",
    section: "Section 6: Executive Endorsement & Final Assessment",
    label: "Key Distinguishers",
    text: "What sets the candidate apart from other top-tier C-suite candidates in the market?",
    type: "textarea",
  },
  {
    id: "q16",
    section: "Section 6: Executive Endorsement & Final Assessment",
    label: "Executive Blind Spots",
    text: "What are the candidate's potential development areas or blind spots when transitioning into a senior role at Moët Hennessy?",
    type: "textarea",
  },
  {
    id: "q17",
    section: "Section 6: Executive Endorsement & Final Assessment",
    label: "Rehire & Board Endorsement",
    text: "Rehire & Board Endorsement",
    type: "radio",
    options: [
      { value: "unconditional", label: "Unconditional Endorsement: I would gladly hire or serve alongside the candidate again in a top executive role." },
      { value: "conditional", label: "Conditional Endorsement: I endorse the candidate with specific parameters." },
      { value: "do_not_endorse", label: "Do Not Endorse." },
    ],
  },
  {
    id: "q18",
    section: "Section 6: Executive Endorsement & Final Assessment",
    label: "Overall Fit Rating",
    text: "On a scale of 1 to 10, how confidently do you rate the candidate's readiness for executive leadership at Moët Hennessy?",
    type: "rating",
  },
];

export const NON_EXECUTIVE_QUESTIONS = [
  // Section 1: Context & Relationship
  {
    id: "q1",
    section: "Section 1: Context & Relationship",
    label: "Relationship Duration & Scope",
    text: "In what capacity did you work with the candidate, and for how long?",
    type: "textarea",
  },
  {
    id: "q2",
    section: "Section 1: Context & Relationship",
    label: "Direct Observation",
    text: "How closely did you oversee or interact with their day-to-day work, key decisions, and professional behavior?",
    type: "textarea",
  },
  // Section 2: Integrity, Discretion & Brand Representation
  {
    id: "q3",
    section: "Section 2: Integrity, Discretion & Brand Representation",
    sectionIntro:
      "Moët Hennessy operates at the pinnacle of luxury, where discretion, high-net-worth client trust, and brand reputation are paramount.",
    label: "Integrity & Discretion",
    text: "Can you describe a scenario where the candidate demonstrated exceptional discretion, integrity, or ethical judgment when handling sensitive information or high-stakes situations?",
    type: "textarea",
  },
  {
    id: "q4",
    section: "Section 2: Integrity, Discretion & Brand Representation",
    label: "Executive Presence",
    text: "How does the candidate conduct themselves when interacting with high-profile clients, executive stakeholders, or external partners?",
    type: "textarea",
  },
  {
    id: "q5",
    section: "Section 2: Integrity, Discretion & Brand Representation",
    label: "Trustworthiness",
    text: "Would you entrust this candidate with key brand assets, confidential strategic plans, or high-value client relationships without direct supervision? Why or why not?",
    type: "textarea",
  },
  // Section 3: Excellence, Resilience & Craftsmanship
  {
    id: "q6",
    section: "Section 3: Excellence, Resilience & Craftsmanship",
    sectionIntro:
      "Excellence, craftsmanship, and attention to detail define the culture of Moët Hennessy and the LVMH group.",
    label: "Standards of Excellence",
    text: "How would you describe the candidate's commitment to quality and attention to detail? Can you give an example where they refused to compromise on quality?",
    type: "textarea",
  },
  {
    id: "q7",
    section: "Section 3: Excellence, Resilience & Craftsmanship",
    label: "Resilience Under Pressure",
    text: "How does the candidate react in fast-paced, high-pressure environments, or when facing sudden disruptions and tight deadlines?",
    type: "textarea",
  },
  {
    id: "q8",
    section: "Section 3: Excellence, Resilience & Craftsmanship",
    label: "Adaptability & Learning Agility",
    text: "How open is the candidate to feedback, and how effectively do they adapt to shifting priorities or unfamiliar cultural contexts?",
    type: "textarea",
  },
  // Section 4: Collaboration & Leadership
  {
    id: "q9",
    section: "Section 4: Collaboration & Leadership",
    label: "Interpersonal & Cultural Fit",
    text: "How would you describe the candidate's ability to build relationships across diverse teams, functions, and international markets?",
    type: "textarea",
  },
  {
    id: "q10",
    section: "Section 4: Collaboration & Leadership",
    label: "Entrepreneurial Mindset",
    text: "Does the candidate show proactive initiative and creative problem-solving, or do they rely heavily on step-by-step instructions?",
    type: "textarea",
  },
  {
    id: "q11",
    section: "Section 4: Collaboration & Leadership",
    label: "Team Dynamics",
    text: "How do peers, direct reports, or cross-functional partners respond to working with the candidate?",
    type: "textarea",
  },
  // Section 5: Overall Evaluation & Endorsement
  {
    id: "q12",
    section: "Section 5: Overall Evaluation & Endorsement",
    label: "Core Strengths",
    text: "What are the candidate's greatest professional and character strengths?",
    type: "textarea",
  },
  {
    id: "q13",
    section: "Section 5: Overall Evaluation & Endorsement",
    label: "Development Areas",
    text: "In what areas might the candidate need coaching, mentorship, or additional support in a demanding new environment?",
    type: "textarea",
  },
  {
    id: "q14",
    section: "Section 5: Overall Evaluation & Endorsement",
    label: "Rehire Status",
    text: "If given the opportunity, would you rehire or choose to work with the candidate again?",
    type: "radio",
    options: [
      { value: "unconditional", label: "Yes, without reservation" },
      { value: "conditional", label: "Yes, with minor caveats" },
      { value: "do_not_endorse", label: "No" },
    ],
  },
  {
    id: "q15",
    section: "Section 5: Overall Evaluation & Endorsement",
    label: "Overall Recommendation",
    text: "On a scale of 1 to 10, how strongly do you endorse the candidate for a position at Moët Hennessy?",
    type: "rating",
  },
];

export const QUESTION_SETS = {
  executive: EXECUTIVE_QUESTIONS,
  non_executive: NON_EXECUTIVE_QUESTIONS,
};
